import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { 
  BookOpenIcon,
  AcademicCapIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  PuzzlePieceIcon,
  Bars3Icon,
  XMarkIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

const Docs: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  // SEO metadata
  const pageUrl = 'https://upmindy.com/docs';
  const pageTitle = 'Documentação Upmindy - Guia Completo de Recursos de Aprendizagem';
  const pageDescription = 'Aprenda a usar todos os recursos do Upmindy: Flashcards com repetição espaçada, Quizzes interativos, Match Game e Controle de Estudos. Guia completo para otimizar seus estudos.';
  const keywords = 'upmindy documentação, flashcards, quizzes, match game, controle de estudos, repetição espaçada, aprendizagem, memorização, estudo inteligente';
  const pageImage = 'https://upmindy.com/upmindy-docs-og.png';

  // Structured data for documentation
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': pageUrl,
    'url': pageUrl,
    'name': pageTitle,
    'description': pageDescription,
    'inLanguage': 'pt-BR',
    'isPartOf': {
      '@type': 'WebSite',
      'name': 'Upmindy',
      'url': 'https://upmindy.com'
    },
    'breadcrumb': {
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': 'Home',
          'item': 'https://upmindy.com'
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'name': 'Documentação',
          'item': pageUrl
        }
      ]
    },
    'mainEntity': {
      '@type': 'ItemList',
      'name': 'Tópicos de Documentação',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': 'Flashcards',
          'url': 'https://upmindy.com/docs/flashcards'
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'name': 'Quizzes',
          'url': 'https://upmindy.com/docs/quizzes'
        },
        {
          '@type': 'ListItem',
          'position': 3,
          'name': 'Match Game',
          'url': 'https://upmindy.com/feature/match-game'
        },
        {
          '@type': 'ListItem',
          'position': 4,
          'name': 'Módulo de Estudos',
          'url': 'https://upmindy.com/docs/studies'
        }
      ]
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'Upmindy',
      'url': 'https://upmindy.com'
    }
  };

  const sections = [
    {
      title: 'Recursos Principais',
      items: [
        { name: 'Visão Geral', href: '/docs', icon: BookOpenIcon },
        { name: 'Flashcards', href: '/docs/flashcards', icon: AcademicCapIcon },
        { name: 'Quizzes', href: '/docs/quizzes', icon: ClipboardDocumentCheckIcon },
        { name: 'Match Game', href: '/feature/match-game', icon: PuzzlePieceIcon },
        { name: 'Módulo de Estudos', href: '/docs/studies', icon: ChartBarIcon },
      ]
    }
  ];

  const isActive = (path: string) => {
    if (path === '/docs') {
      return location.pathname === '/docs';
    }
    return location.pathname.startsWith(path);
  };

  const isOverview = location.pathname === '/docs';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={pageUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Upmindy" />
        <meta property="og:locale" content="pt_BR" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />
        
        {/* Additional Meta Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="Portuguese" />
        <meta name="author" content="Upmindy" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Mobile Header with Hamburger Menu */}
      <div className="lg:hidden sticky top-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-3">
            <BookOpenIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              Documentação
            </h1>
          </div>
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Abrir menu"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-gray-900/50 backdrop-blur-sm"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Sidebar */}
          <div className={`
            fixed lg:static inset-y-0 left-0 z-50 lg:z-auto
            w-80 lg:w-auto lg:col-span-3
            transform lg:transform-none transition-transform duration-300 ease-in-out
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            bg-gray-50 dark:bg-gray-900 lg:bg-transparent
            overflow-y-auto
            border-r border-gray-200 dark:border-gray-700 lg:border-0
          `}>
            <div className="lg:sticky lg:top-8 p-4 lg:p-0">
              {/* Mobile Close Button */}
              <div className="lg:hidden flex items-center justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Menu</h2>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  aria-label="Fechar menu"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              {/* Quick Access Section */}
              <div className="mb-8 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 uppercase tracking-wider mb-3">
                  Acesso Rápido
                </h3>
                <div className="space-y-2">
                  <Link
                    to="/"
                    onClick={() => setIsSidebarOpen(false)}
                    className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200 border border-gray-300 dark:border-gray-600"
                  >
                    <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Voltar ao Site
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsSidebarOpen(false)}
                    className="flex items-center w-full px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg transition-colors duration-200 border border-blue-200 dark:border-blue-700"
                  >
                    <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Criar Conta
                  </Link>
                  <Link
                    to={user ? "/home" : "/login"}
                    onClick={() => setIsSidebarOpen(false)}
                    className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200 border border-gray-200 dark:border-gray-700"
                  >
                    <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013 3v1" />
                    </svg>
                    {user ? 'Ir para App' : 'Fazer Login'}
                  </Link>
                </div>
              </div>
              
              <nav className="space-y-8">
                {sections.map((section) => (
                  <div key={section.title}>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-3">
                      {section.title}
                    </h3>
                    <ul className="space-y-1">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              onClick={() => setIsSidebarOpen(false)}
                              className={`${
                                isActive(item.href)
                                  ? 'bg-primary-50 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-700'
                                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 border-transparent'
                              } group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 border`}
                            >
                              <Icon className={`mr-3 h-5 w-5 ${
                                isActive(item.href) 
                                  ? 'text-primary-600 dark:text-primary-400' 
                                  : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300'
                              }`} />
                              {item.name}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </nav>
            </div>
          </div>

          {/* Main content */}
          <div className="mt-0 lg:mt-0 lg:col-span-9">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 lg:p-8">
              {isOverview ? (
                <div className="prose dark:prose-invert max-w-none">
                  <div className="mb-10">
                    <div className="text-center mb-8">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 dark:from-primary-400 dark:to-primary-500 rounded-2xl mb-6 shadow-lg">
                        <BookOpenIcon className="h-8 w-8 text-white" />
                      </div>
                      <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent mb-3">
                        Upmindy
                      </h1>
                      <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto">
                        Aprenda como aproveitar ao máximo sua jornada de aprendizagem com os 
                        recursos do Upmindy: Flashcards com repetição espaçada, Quizzes interativos,
                        Match Game e Controle de Estudos completo.
                      </p>
                    </div>
                    
                    <div className="bg-gradient-to-r from-primary-50 via-blue-50 to-indigo-50 dark:from-primary-900/20 dark:via-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 border border-primary-200 dark:border-primary-800 mb-8">
                      <div className="flex items-center mb-4">
                        <div className="flex-shrink-0 p-2 bg-green-500 rounded-lg">
                          <RocketLaunchIcon className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="ml-3 text-lg font-semibold text-gray-900 dark:text-white">
                          Comece Sua Jornada de Aprendizagem
                        </h3>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        Esta documentação irá guiá-lo através de todas as funcionalidades do Upmindy, 
                        desde os conceitos básicos até técnicas avançadas de otimização de estudos. 
                        Escolha um tópico abaixo para começar!
                      </p>
                    </div>

                    {/* Call to Action for Account Creation */}
                    <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-emerald-900/10 dark:via-teal-900/10 dark:to-cyan-900/10 rounded-2xl p-8 border border-emerald-200 dark:border-emerald-800 mb-10 shadow-sm">
                      <div className="text-center">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 dark:from-emerald-400 dark:to-teal-500 rounded-xl mb-4 shadow-lg">
                          <svg className="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                          Pronto para começar a estudar?
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto leading-relaxed">
                          Crie sua conta gratuita e comece a aprender com nosso sistema inteligente de repetição espaçada. 
                          Já tem uma conta? Faça login para continuar seus estudos.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                          <Link
                            to="/register"
                            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                          >
                            <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Criar conta gratuita
                          </Link>
                          <Link
                            to={user ? "/home" : "/login"}
                            className="inline-flex items-center px-6 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-semibold rounded-lg border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                          >
                            <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                            </svg>
                            {user ? 'Ir para App' : 'Fazer login'}
                          </Link>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                          100% gratuito para começar • Sem cartão de crédito necessário
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 not-prose">
                    <div className="group bg-gradient-to-br from-blue-50 via-blue-25 to-indigo-50 dark:from-gray-900 dark:via-gray-850 dark:to-gray-800 p-8 rounded-2xl border border-blue-100 dark:border-gray-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-20 h-20 bg-blue-100 dark:bg-blue-900/20 rounded-full -translate-y-10 translate-x-10 opacity-50"></div>
                      <div className="relative">
                        <div className="flex items-center mb-6">
                          <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-400 dark:to-blue-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                            <AcademicCapIcon className="h-7 w-7 text-white" />
                          </div>
                          <h3 className="ml-4 text-xl font-bold text-gray-900 dark:text-gray-100">
                            Flashcards
                          </h3>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                          Cartões de estudo com repetição espaçada inteligente. Memorize vocabulário, conceitos e fatos de forma eficiente com nosso algoritmo cientificamente comprovado.
                        </p>
                        <Link
                          to="/docs/flashcards"
                          className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold transition-colors group-hover:translate-x-1 duration-300"
                        >
                          Saiba mais 
                          <svg className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </Link>
                      </div>
                    </div>

                    <div className="group bg-gradient-to-br from-purple-50 via-purple-25 to-pink-50 dark:from-gray-900 dark:via-gray-850 dark:to-gray-800 p-8 rounded-2xl border border-purple-100 dark:border-gray-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-20 h-20 bg-purple-100 dark:bg-purple-900/20 rounded-full -translate-y-10 translate-x-10 opacity-50"></div>
                      <div className="relative">
                        <div className="flex items-center mb-6">
                          <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 dark:from-purple-400 dark:to-purple-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                            <ClipboardDocumentCheckIcon className="h-7 w-7 text-white" />
                          </div>
                          <h3 className="ml-4 text-xl font-bold text-gray-900 dark:text-gray-100">
                            Quizzes
                          </h3>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                          Testes de múltipla escolha com sistema de revisão adaptativa. Avalie seu conhecimento e reforçe seu aprendizado com feedback imediato.
                        </p>
                        <Link
                          to="/docs/quizzes"
                          className="inline-flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-semibold transition-colors group-hover:translate-x-1 duration-300"
                        >
                          Saiba mais 
                          <svg className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </Link>
                      </div>
                    </div>

                    <div className="group bg-gradient-to-br from-pink-50 via-rose-50 to-fuchsia-50 dark:from-gray-900 dark:via-gray-850 dark:to-gray-800 p-8 rounded-2xl border border-pink-100 dark:border-gray-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-20 h-20 bg-pink-100 dark:bg-pink-900/20 rounded-full -translate-y-10 translate-x-10 opacity-50"></div>
                      <div className="relative">
                        <div className="flex items-center mb-6">
                          <div className="p-3 bg-gradient-to-br from-pink-500 to-rose-600 dark:from-pink-400 dark:to-rose-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                            <PuzzlePieceIcon className="h-7 w-7 text-white" />
                          </div>
                          <h3 className="ml-4 text-xl font-bold text-gray-900 dark:text-gray-100">
                            Match Game
                          </h3>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                          Jogo de memória educativo para parear conceitos relacionados. Uma forma divertida e eficaz de reforçar seu aprendizado através do jogo.
                        </p>
                        <Link
                          to="/feature/match-game"
                          className="inline-flex items-center text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 font-semibold transition-colors group-hover:translate-x-1 duration-300"
                        >
                          Saiba mais 
                          <svg className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </Link>
                      </div>
                    </div>

                    <div className="group bg-gradient-to-br from-green-50 via-green-25 to-emerald-50 dark:from-gray-900 dark:via-gray-850 dark:to-gray-800 p-8 rounded-2xl border border-green-100 dark:border-gray-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-20 h-20 bg-green-100 dark:bg-green-900/20 rounded-full -translate-y-10 translate-x-10 opacity-50"></div>
                      <div className="relative">
                        <div className="flex items-center mb-6">
                          <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 dark:from-green-400 dark:to-green-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                            <ChartBarIcon className="h-7 w-7 text-white" />
                          </div>
                          <h3 className="ml-4 text-xl font-bold text-gray-900 dark:text-gray-100">
                            Módulo de Estudos
                          </h3>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                          Sistema completo de gerenciamento de estudos. Organize recursos, acompanhe progresso, defina prioridades e mantenha seus estudos estruturados.
                        </p>
                        <Link
                          to="/docs/studies"
                          className="inline-flex items-center text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-semibold transition-colors group-hover:translate-x-1 duration-300"
                        >
                          Saiba mais 
                          <svg className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <Outlet />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Docs;
